/**
 * TAX-EMAIL-CONFIRM-001 — confirmation / awareness dialog for the jurisdiction-scoped Tax Collector Email action.
 * `preflight` = result of GET /orders/{id}/tax-email/preflight (ok → Confirm & Send; otherwise blocking → Close).
 */
import React from 'react';
import { AlertTriangle, Loader2, Mail, Send } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const Row = ({ label, value, testId }) => (
  <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-900 break-words" data-testid={testId}>{value || '—'}</span>
  </div>
);

export const TaxEmailConfirmDialog = ({ open, preflight, loading, error, onCancel, onConfirm, submitting }) => {
  const ready = !!preflight?.ok;
  const title = loading ? 'Checking Tax Collector email…' : error ? 'Unable to Verify Email Configuration'
    : ready ? 'Send Email to Tax Collector?' : (preflight?.title || 'Email Cannot Be Sent');
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && !submitting) onCancel(); }}>
      <AlertDialogContent className="max-w-lg" data-testid="tax-email-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2" data-testid="tax-email-confirm-title">
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              : ready ? <Mail className="h-4 w-4 text-indigo-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-1 text-left">
              {loading && <p className="text-sm text-slate-500">Verifying jurisdiction, Parcel/APN and recipient…</p>}
              {!loading && error && (
                <p className="text-sm text-rose-700" data-testid="tax-email-confirm-error">{error} No email has been sent and the order was not changed.</p>
              )}
              {!loading && !error && ready && (
                <>
                  <p className="text-sm text-slate-600">Please confirm that you want to send this email. Only the jurisdiction below will be emailed.</p>
                  <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <Row label="Jurisdiction" value={preflight.jurisdiction} testId="tax-email-confirm-jurisdiction" />
                    <Row label="Recipient" value={preflight.recipient} testId="tax-email-confirm-recipient" />
                    <Row label="Parcel/APN" value={(preflight.parcels || []).join(', ')} testId="tax-email-confirm-parcels" />
                    <Row label="From" value={preflight.from_name ? `${preflight.from_name} <${preflight.from_email}>` : preflight.from_email} testId="tax-email-confirm-from" />
                    <Row label="CC" value={`${preflight.cc_count || 0} internal recipient${preflight.cc_count === 1 ? '' : 's'}`} testId="tax-email-confirm-cc" />
                  </div>
                </>
              )}
              {!loading && !error && preflight && !ready && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="tax-email-confirm-blocked">
                  <p>{preflight.message}</p>
                  {preflight.missing_parcels?.length > 0 && (
                    <ul className="ml-4 mt-1 list-disc">{preflight.missing_parcels.map(m => <li key={m.index}>{m.label}{m.address ? ` (${m.address})` : ''}</li>)}</ul>
                  )}
                  <p className="mt-2 text-xs text-amber-700">No email was sent and the order was not moved to Pending.</p>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {ready && !loading && !error ? (
            <>
              <AlertDialogCancel disabled={submitting} data-testid="tax-email-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={submitting} data-testid="tax-email-confirm-send">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Confirm &amp; Send
              </AlertDialogAction>
            </>
          ) : (
            <AlertDialogCancel disabled={loading} data-testid="tax-email-confirm-close">Close</AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
